import React, { useState, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import baseurl from '../Baseurl/baseurl';
import {
  Button,
  Card,
  FormField,
  Input,
  Select,
  Textarea,
  Heading,
  Eyebrow,
  Container,
  Divider,
} from '../ui';

const BusinessDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [businessProfileId, setBusinessProfileId] = useState(null);
  const [expanded, setExpanded] = useState('basic');
  const [registrationType, setRegistrationType] = useState('');
  const [customRegistrationType, setCustomRegistrationType] = useState('');
  const [showCustomRegistration, setShowCustomRegistration] = useState(false);

  const [formData, setFormData] = useState({
    business_type: 'self-employed',
    category_id: '',
    company_name: '',
    business_registration_type: '',
    about: '',
    company_address: '',
    city: '',
    state: '',
    zip_code: '',
    business_starting_year: '',
    staff_size: '',
    business_work_contract: '',
    email: '',
    source: '',
    tags: '',
    website: '',
    google_link: '',
    facebook_link: '',
    instagram_link: '',
    linkedin_link: '',
    designation: '',
    salary: '',
    location: '',
    experience: '',
    profileImage: null,
    mediaGallery: [],
  });

  const [errors, setErrors] = useState({});

  // Business registration type options
  const registrationTypeOptions = [ "proprietor", "Partnership", "Others"
  ];

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Email validation function
  const validateEmail = (email) => {
    if (!email) return true; // Empty email is valid (optional field)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form fields
  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'email':
        if (value && !validateEmail(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'business_starting_year':
        if (value && (value < 1900 || value > new Date().getFullYear())) {
          error = 'Please enter a valid year';
        }
        break;
      case 'zip_code':
        if (value && !/^\d{5,6}(-\d{4})?$/.test(value)) {
          error = 'Please enter a valid zip code';
        }
        break;
      default:
        break;
    }
    return error;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${baseurl}/api/category/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch categories: ${res.status}`);
        }
        const data = await res.json();
        if (data.success) {
          setCategories(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load categories',
          severity: 'error'
        });
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }
        const res = await fetch(`${baseurl}/api/member/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch member data: ${res.status}`);
        }
        const data = await res.json();
        const businessProfiles = data?.data?.BusinessProfiles;
        if (!businessProfiles || businessProfiles.length === 0) {
          throw new Error('Business profile not found.');
        }
        const business = businessProfiles[0];
        setBusinessProfileId(business.id);

        // Check if business registration type is one of the predefined options
        const isPredefinedType = registrationTypeOptions.includes(business.business_registration_type);

        setFormData({
          business_type: business.business_type || 'self-employed',
          category_id: business.category_id || '',
          company_name: business.company_name || '',
          business_registration_type: business.business_registration_type || '',
          about: business.about || '',
          company_address: business.company_address || '',
          city: business.city || '',
          state: business.state || '',
          zip_code: business.zip_code || '',
          business_starting_year: business.business_starting_year || '',
          staff_size: business.staff_size || '',
          business_work_contract: business.business_work_contract || '',
          email: business.email || '',
          source: business.source || '',
          tags: business.tags || '',
          website: business.website || '',
          google_link: business.google_link || '',
          facebook_link: business.facebook_link || '',
          instagram_link: business.instagram_link || '',
          linkedin_link: business.linkedin_link || '',
          designation: business.designation || '',
          salary: business.salary || '',
          location: business.location || '',
          experience: business.experience || '',
          profileImage: business.business_profile_image || null,
          mediaGallery: business.media_gallery ? business.media_gallery.split(',') : [],
        });

        // Set registration type state
        if (business.business_registration_type) {
          if (isPredefinedType) {
            setRegistrationType(business.business_registration_type);
            setShowCustomRegistration(false);
          } else {
            setRegistrationType('Others');
            setCustomRegistrationType(business.business_registration_type);
            setShowCustomRegistration(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch business data:', error);
        setSnackbar({
          open: true,
          message: error.message || t('failedToLoadData'),
          severity: 'error'
        });
      }
    };
    fetchData();
  }, [id, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle registration type change
    if (name === 'business_registration_type') {
      setRegistrationType(value);
      if (value === 'Others') {
        setShowCustomRegistration(true);
        // Keep the existing custom value if it exists
        if (!customRegistrationType) {
          setCustomRegistrationType('');
        }
      } else {
        setShowCustomRegistration(false);
        setCustomRegistrationType('');
        // Update form data with the selected predefined type
        setFormData(prev => ({
          ...prev,
          business_registration_type: value
        }));
      }
    }
    // Handle custom registration type input
    else if (name === 'custom_registration_type') {
      setCustomRegistrationType(value);
      // Update form data with the custom value
      setFormData(prev => ({
        ...prev,
        business_registration_type: value
      }));
    }
    // Handle all other inputs
    else {
      // Validate the field as user types
      const error = validateField(name, value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (name === 'profileImage') {
      setFormData(prev => ({ ...prev, profileImage: files[0] }));
    } else if (name === 'mediaGallery') {
      setFormData(prev => ({ ...prev, mediaGallery: Array.from(files) }));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const validateForm = () => {
    const newErrors = {};
    // Validate email
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    // Validate business starting year
    if (formData.business_starting_year &&
      (formData.business_starting_year < 1900 || formData.business_starting_year > new Date().getFullYear())) {
      newErrors.business_starting_year = 'Please enter a valid year';
    }
    // Validate zip code
    if (formData.zip_code && !/^\d{5,6}(-\d{4})?$/.test(formData.zip_code)) {
      newErrors.zip_code = 'Please enter a valid zip code';
    }
    // Validate custom registration type if "Others" is selected
    if (registrationType === 'Others' && !customRegistrationType.trim()) {
      newErrors.custom_registration_type = 'Please specify the business registration type';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    try {
      // Validate form before submission
      if (!validateForm()) {
        setSnackbar({
          open: true,
          message: 'Please fix the validation errors before submitting',
          severity: 'error'
        });
        return;
      }
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error(t('pleaseLogin'));
      if (!businessProfileId) throw new Error(t('businessProfileNotFound'));
      const formDataToSend = new FormData();
      formDataToSend.append('business_type', formData.business_type);
      formDataToSend.append('category_id', formData.category_id);
      formDataToSend.append('company_name', formData.company_name);

      // Handle business registration type
      if (registrationType === 'Others') {
        formDataToSend.append('business_registration_type', customRegistrationType);
      } else {
        formDataToSend.append('business_registration_type', registrationType);
      }

      // Self-employed specific fields
      if (formData.business_type === 'self-employed') {
        formDataToSend.append('about', formData.about);
        formDataToSend.append('company_address', formData.company_address);
        formDataToSend.append('city', formData.city);
        formDataToSend.append('state', formData.state);
        formDataToSend.append('zip_code', formData.zip_code);
        formDataToSend.append('business_starting_year', formData.business_starting_year);
        formDataToSend.append('business_work_contract', formData.business_work_contract);
      }
      // Salary specific fields
      if (formData.business_type === 'salary') {
        formDataToSend.append('designation', formData.designation);
        formDataToSend.append('salary', formData.salary);
        formDataToSend.append('location', formData.location);
        formDataToSend.append('experience', formData.experience);
      }
      // Common fields - only add email if it's valid
      if (formData.business_type === 'self-employed') {
        formDataToSend.append('staff_size', formData.staff_size);
        if (formData.email && validateEmail(formData.email)) {
          formDataToSend.append('email', formData.email);
        }
        formDataToSend.append('source', formData.source);
        formDataToSend.append('tags', formData.tags);
        formDataToSend.append('website', formData.website);
        formDataToSend.append('google_link', formData.google_link);
        formDataToSend.append('facebook_link', formData.facebook_link);
        formDataToSend.append('instagram_link', formData.instagram_link);
        formDataToSend.append('linkedin_link', formData.linkedin_link);
      }
      if (formData.profileImage instanceof File) {
        formDataToSend.append('business_profile_image', formData.profileImage);
      }
      if (formData.mediaGallery.length > 0 && formData.mediaGallery[0] instanceof File) {
        formData.mediaGallery.forEach(file => {
          formDataToSend.append('media_gallery', file);
        });
      }
      // Log the request for debugging
      console.log('Updating business profile with ID:', businessProfileId);
      console.log('API Endpoint:', `${baseurl}/api/business-profile/update/${businessProfileId}`);
      const response = await fetch(`${baseurl}/api/business-profile/update/${businessProfileId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend
      });
      // Check response status before parsing JSON
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', response.status, errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || data.msg || t('updateFailed'));
      }
      setSnackbar({
        open: true,
        message: data.message || data.msg || t('updateSuccess'),
        severity: 'success'
      });
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      console.error('Update error details:', error);
      setSnackbar({
        open: true,
        message: error.message || t('updateFailed'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const businessTypeOptions = [
    { value: 'self-employed', label: 'Self Employed' },
    { value: 'business', label: 'Business' },
    { value: 'salary', label: 'Salary' },
  ];

  return (
    <div className="min-h-screen bg-bg pb-24">
      <Header />
      {/* Dark navy hero / back band */}
      <div className="on-dark bg-navy">
        <Container className="flex items-center gap-3 py-5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line/15 bg-surface/10 text-ink transition-colors hover:bg-surface/20"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <Eyebrow>Business</Eyebrow>
            <Heading variant="title" className="mt-1">
              {t('businessDetails')} <em className="kbc-serif">details</em>
            </Heading>
          </div>
        </Container>
      </div>

      <Container className="mt-8 space-y-6">
        {/* Basic Details Accordion */}
        <Card padding="none" className="overflow-hidden">
          <button
            type="button"
            onClick={(e) => handleAccordionChange('basic')(e, expanded !== 'basic')}
            className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
          >
            <span className="font-display text-lg font-semibold text-ink">Basic Details</span>
            <ChevronDown
              className={`h-5 w-5 text-muted transition-transform duration-300 ${expanded === 'basic' ? 'rotate-180' : ''}`}
            />
          </button>
          {expanded === 'basic' && (
            <div className="space-y-5 border-t border-line/12 px-6 py-6">
              {/* Business Type Selection */}
              <FormField label="Business Type">
                <div className="flex flex-wrap gap-3">
                  {businessTypeOptions.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2.5 text-sm font-medium transition-all ${
                        formData.business_type === opt.value
                          ? 'border-primary/50 bg-primary/10 text-ink'
                          : 'border-line/15 bg-surface text-text hover:border-primary/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="business_type"
                        value={opt.value}
                        checked={formData.business_type === opt.value}
                        onChange={handleInputChange}
                        className="h-4 w-4 accent-primary"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </FormField>

              {/* Category Selection - Only for self-employed */}
              {(formData.business_type === 'self-employed' || formData.business_type === 'business') && (
                <FormField label="Category" htmlFor="category_id">
                  <Select
                    id="category_id"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.cid} value={category.cid}>
                        {category.category_name}
                      </option>
                    ))}
                  </Select>
                </FormField>
              )}

              {/* Company Name - Common for both types */}
              <FormField label="Company Name" htmlFor="company_name">
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                />
              </FormField>

              {/* Conditional Fields Based on Business Type */}
              {formData.business_type === 'self-employed' ? (
                <>
                  {/* Business Registration Type */}
                  <FormField label="Business Registration Type" htmlFor="business_registration_type">
                    <Select
                      id="business_registration_type"
                      name="business_registration_type"
                      value={registrationType}
                      onChange={handleInputChange}
                    >
                      <option value="">Select type</option>
                      {registrationTypeOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  {/* Custom Registration Type Input - Only shown when "Others" is selected */}
                  {showCustomRegistration && (
                    <FormField
                      label="Specify Business Registration Type"
                      htmlFor="custom_registration_type"
                      error={errors.custom_registration_type}
                    >
                      <Input
                        id="custom_registration_type"
                        name="custom_registration_type"
                        value={customRegistrationType}
                        onChange={handleInputChange}
                        error={!!errors.custom_registration_type}
                      />
                    </FormField>
                  )}

                  {/* About Business */}
                  <FormField label="About Business" htmlFor="about">
                    <Textarea
                      id="about"
                      name="about"
                      value={formData.about}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </FormField>
                </>
              ) : (
                <>
                  {/* Salary-specific basic fields */}
                  <FormField label="Designation" htmlFor="designation">
                    <Input
                      id="designation"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                    />
                  </FormField>
                  <FormField label="Salary" htmlFor="salary">
                    <Input
                      id="salary"
                      name="salary"
                      value={formData.salary}
                      onChange={handleInputChange}
                    />
                  </FormField>
                  <FormField label="Location" htmlFor="location">
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                    />
                  </FormField>
                  <FormField label="Experience" htmlFor="experience">
                    <Input
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                    />
                  </FormField>
                </>
              )}
            </div>
          )}
        </Card>

        {/* Advanced Details Accordion - Only for self-employed */}
        {(formData.business_type === 'self-employed' || formData.business_type === 'business') && (
          <Card padding="none" className="overflow-hidden">
            <button
              type="button"
              onClick={(e) => handleAccordionChange('advanced')(e, expanded !== 'advanced')}
              className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
            >
              <span className="font-display text-lg font-semibold text-ink">Advanced Details</span>
              <ChevronDown
                className={`h-5 w-5 text-muted transition-transform duration-300 ${expanded === 'advanced' ? 'rotate-180' : ''}`}
              />
            </button>
            {expanded === 'advanced' && (
              <div className="space-y-5 border-t border-line/12 px-6 py-6">
                {/* Address Section */}
                <Eyebrow>Address Information</Eyebrow>
                <FormField label="Company Address" htmlFor="company_address">
                  <Textarea
                    id="company_address"
                    name="company_address"
                    value={formData.company_address}
                    onChange={handleInputChange}
                    rows={2}
                  />
                </FormField>
                <div className="grid gap-4 sm:grid-cols-3">
                  <FormField label="City" htmlFor="city">
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                    />
                  </FormField>
                  <FormField label="State" htmlFor="state">
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                    />
                  </FormField>
                  <FormField label="Pin Code" htmlFor="zip_code" error={errors.zip_code}>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      error={!!errors.zip_code}
                    />
                  </FormField>
                </div>
                <FormField
                  label="Business Starting Year"
                  htmlFor="business_starting_year"
                  error={errors.business_starting_year}
                >
                  <Input
                    id="business_starting_year"
                    name="business_starting_year"
                    value={formData.business_starting_year}
                    onChange={handleInputChange}
                    error={!!errors.business_starting_year}
                  />
                </FormField>
                <FormField label="Business Work Contract" htmlFor="business_work_contract">
                  <Textarea
                    id="business_work_contract"
                    name="business_work_contract"
                    value={formData.business_work_contract}
                    onChange={handleInputChange}
                    rows={2}
                  />
                </FormField>

                <Divider />

                {/* Common Advanced Fields */}
                <Eyebrow>Additional Information</Eyebrow>
                <FormField label="Staff Size" htmlFor="staff_size">
                  <Input
                    id="staff_size"
                    name="staff_size"
                    value={formData.staff_size}
                    onChange={handleInputChange}
                  />
                </FormField>
                <FormField label="Email" htmlFor="email" error={errors.email}>
                  <Input
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={!!errors.email}
                  />
                </FormField>
                <FormField label="Referred By" htmlFor="source">
                  <Input
                    id="source"
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                  />
                </FormField>

                <Divider />

                {/* Social Media Links */}
                <Eyebrow>Social Media &amp; Links</Eyebrow>
                <FormField label="Tags (comma separated)" htmlFor="tags">
                  <Input
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                  />
                </FormField>
                <FormField label="Website" htmlFor="website">
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                  />
                </FormField>
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField label="Google Link" htmlFor="google_link">
                    <Input
                      id="google_link"
                      name="google_link"
                      value={formData.google_link}
                      onChange={handleInputChange}
                    />
                  </FormField>
                  <FormField label="Facebook Link" htmlFor="facebook_link">
                    <Input
                      id="facebook_link"
                      name="facebook_link"
                      value={formData.facebook_link}
                      onChange={handleInputChange}
                    />
                  </FormField>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField label="Instagram Link" htmlFor="instagram_link">
                    <Input
                      id="instagram_link"
                      name="instagram_link"
                      value={formData.instagram_link}
                      onChange={handleInputChange}
                    />
                  </FormField>
                  <FormField label="LinkedIn Link" htmlFor="linkedin_link">
                    <Input
                      id="linkedin_link"
                      name="linkedin_link"
                      value={formData.linkedin_link}
                      onChange={handleInputChange}
                    />
                  </FormField>
                </div>

                <Divider />

                {/* Media Section */}
                <Eyebrow>Media</Eyebrow>
                {/* Profile Image */}
                <div className="text-left">
                  <p className="mb-2 text-[0.82rem] font-semibold text-ink">Business Profile Image:</p>
                  <input
                    type="file"
                    accept="image/*"
                    name="profileImage"
                    onChange={handleFileChange}
                    className="mb-4 block w-full text-sm text-text file:mr-4 file:rounded-md file:border-0 file:bg-primary/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary hover:file:bg-primary/15"
                  />
                  {formData.profileImage && (
                    <div className="text-left">
                      <img
                        src={formData.profileImage instanceof File
                          ? URL.createObjectURL(formData.profileImage)
                          : `${baseurl}/${formData.profileImage}`}
                        alt="Business"
                        className="block h-auto w-full max-w-[100px] rounded-md border border-line/15"
                      />
                    </div>
                  )}
                </div>
                {/* Media Gallery */}
                <div className="mt-4 text-left">
                  <p className="mb-2 text-[0.82rem] font-semibold text-ink">Upload images and videos:</p>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    name="mediaGallery"
                    onChange={handleFileChange}
                    className="mb-4 block w-full text-sm text-text file:mr-4 file:rounded-md file:border-0 file:bg-primary/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary hover:file:bg-primary/15"
                  />
                  <div className="flex flex-wrap justify-start gap-4">
                    {formData.mediaGallery.map((media, index) => {
                      const isVideo = media instanceof File
                        ? media.type.startsWith('video/')
                        : media.match(/\.(mp4|webm|ogg)$/i);
                      const mediaUrl = media instanceof File
                        ? URL.createObjectURL(media)
                        : `${baseurl}/${media}`;
                      return isVideo ? (
                        <div key={index} className="text-left">
                          <video
                            src={mediaUrl}
                            width="150"
                            height="130"
                            controls
                            className="block rounded-md border border-line/15"
                          />
                        </div>
                      ) : (
                        <div key={index} className="text-left">
                          <img
                            src={mediaUrl}
                            alt={`media-${index}`}
                            width="150"
                            className="block rounded-md border border-line/15 object-cover"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        <Button
          fullWidth
          size="lg"
          onClick={handleSubmit}
          loading={loading}
          disabled={loading || !businessProfileId}
        >
          {loading ? 'Saving…' : t('updateChanges')}
        </Button>
      </Container>

      <Footer />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BusinessDetailsPage;
